import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
  YearSelect,
  MonthSelect,
  ChartOfAccountsSelect,
  FormatDateToMDY
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import ExcelJS from 'exceljs';
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';

export default function BIRReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Variables 
  const [company, setCompany] = useState(null);
  // const today = new Date().toISOString().split("T")[0];
  const [glAccount, setGLAccount] = useState(null);
  const [monthFrom, setMonthFrom] = useState(null);
  const [monthTo, setMonthTo] = useState(null);
  const [year, setYear] = useState(null);

  const [showGLAccountSelectForm, setShowGLAccountSelectForm] = useState(false);
  const openGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(true);
  }
  const closeGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(false);
  }
//#endregion






//#region Generate
  const fetchReportData = useCallback(async () => {
    const body = {
      glAccountId: glAccount?.id,
      monthFrom: monthFrom,
      monthTo: monthTo,
      year: year,
    }

    try {
      // const response = await axios.post(`${apiUrl}/api/Report/report-data/statement-of-account`, body, {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/bank-reconciliation`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [monthFrom, monthTo, glAccount, year]);

  // PDF
  const generateBankReconciliationDetailed = async () => {
      if ( !monthFrom || !monthTo || !year || !company) {
        setFailMessage("Please fill up all fields");
        return;
      }
  
      setLoading(true);
      const allData = await fetchReportData();
      const _data = allData.detailed;
  
      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }
  
      try {
        const pdfUrl = `${window.location.origin}/docs/BANK_RECONCILIATION_DETAILED.pdf`;
        const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
        const templateDoc = await PDFDocument.load(templateBytes);
        
        const response = await fetch(pdfUrl);
        const pdfBytes = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);
  
        pdfDoc.registerFontkit(fontkit);
        const fontResponse = await fetch("/calibri.ttf");
        const fontBytes = await fontResponse.arrayBuffer();
        const calibriFont = await pdfDoc.embedFont(fontBytes);
  
        const boldResponse = await fetch("/calibrib.ttf");
        const boldBytes = await boldResponse.arrayBuffer();
        const calibriBold = await pdfDoc.embedFont(boldBytes);
  
        const truncateText = (text, maxWidth, font, fontSize) => {
          if (!text) return "";
          let currentWidth = font.widthOfTextAtSize(text, fontSize);
          if (currentWidth <= maxWidth) return text;
          const ellipsis = "...";
          let truncated = text;
          while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
            truncated = truncated.slice(0, -1);
          }
          return truncated + ellipsis;
        };
  
        const drawHeader = (targetPage) => {
          targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
          targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 8, font: calibriFont });
          targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 8, font: calibriFont });
  
          targetPage.drawText(`${glAccount.accountNumber ?? ""} - ${glAccount.accountName ?? ""}`, { x: 68, y: 515, size: 8, font: calibriFont });
          targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 83, y: 506, size: 8, font: calibriFont });
          targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 495, size: 8, font: calibriFont });
        };
  
        let pages = pdfDoc.getPages();
        let currentPage = pages[0];
        drawHeader(currentPage);
  
        let yPosition = 406;
        const bottomThreshold = 80;
        const lineHeight = 12;
  
        // Helper to handle page breaks
        const addNewPage = async () => {
          const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
          currentPage = pdfDoc.addPage(templatePage);
          drawHeader(currentPage);
          yPosition = 406;
        };

        currentPage.drawText(`${FormatValueToDecimal(_data.totalDebits)}`, { x: 211, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalCredits)}`, { x: 263, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalBalance)}`, { x: 321, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalAmountCleared)}`, { x: 380, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalOutstanding)}`, { x: 554, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalInTransit)}`, { x: 605, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalCreditMemo)}`, { x: 656, y: 475, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.totalDebitMemo)}`, { x: 723, y: 475, size: 8, font: calibriFont });
  
        for (const record of _data.transactions) {
          // --- PAGE BREAK CHECK FOR ITEM ROW ---
          if (yPosition - lineHeight < bottomThreshold) {
            await addNewPage();
          }

          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(FormatDateToMDY(record.date), { x: 40, y: yPosition, size: 8, font: calibriFont });

          const truncatedRef = truncateText(record?.narrative ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedRef, { x: 120, y: yPosition, size: 8, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(record.debit)}`, { x: 211, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.credit)}`, { x: 263, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.balance)}`, { x: 321, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.amountCleared)}`, { x: 380, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(FormatDateToMDY(record.dateCleared), { x: 445, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.outstandingCheck)}`, { x: 554, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.depositInTransit)}`, { x: 605, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.creditMemo)}`, { x: 656, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.debitMemo)}`, { x: 723, y: yPosition, size: 8, font: calibriFont });

          yPosition -= lineHeight;
        }
  
        // --- FOOTER SECTION (GRAND TOTAL) ---
        if (yPosition < 100) {
          await addNewPage();
        }
  
        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 765, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`  `, { x: 35, y: yPosition, size: 9, font: calibriBold });
  
        // --- PAGINATION ---
        const allPages = pdfDoc.getPages();
        const totalPages = allPages.length;
        allPages.forEach((page, index) => {
          page.drawText(`Page ${index + 1} of ${totalPages}`, {
            x: 680,
            y: 580,
            size: 10,
            font: calibriBold,
          });
        });
  
        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `BANK_RECONCILIATION_DETAILED.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
  
      } catch (error) {
        console.error("Error:", error);
        setFailMessage("Error processing PDF");
      }
      setLoading(false);
  };

  const generateBankReconciliationSummary = async () => {
      if ( !monthFrom || !monthTo || !year || !company) {
        setFailMessage("Please fill up all fields");
        return;
      }
  
      setLoading(true);
      const allData = await fetchReportData();
      const _data = allData.summary;
  
      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }
  
      try {
        const pdfUrl = `${window.location.origin}/docs/BANK_RECONCILIATION_SUMMARY.pdf`;
        const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
        const templateDoc = await PDFDocument.load(templateBytes);
        
        const response = await fetch(pdfUrl);
        const pdfBytes = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);
  
        pdfDoc.registerFontkit(fontkit);
        const fontResponse = await fetch("/calibri.ttf");
        const fontBytes = await fontResponse.arrayBuffer();
        const calibriFont = await pdfDoc.embedFont(fontBytes);
  
        const boldResponse = await fetch("/calibrib.ttf");
        const boldBytes = await boldResponse.arrayBuffer();
        const calibriBold = await pdfDoc.embedFont(boldBytes);
  
        const truncateText = (text, maxWidth, font, fontSize) => {
          if (!text) return "";
          let currentWidth = font.widthOfTextAtSize(text, fontSize);
          if (currentWidth <= maxWidth) return text;
          const ellipsis = "...";
          let truncated = text;
          while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
            truncated = truncated.slice(0, -1);
          }
          return truncated + ellipsis;
        };
  
        const drawHeader = (targetPage) => {
          targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
          targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 8, font: calibriFont });
          targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 8, font: calibriFont });
  
          targetPage.drawText(`${glAccount.accountNumber ?? ""} - ${glAccount.accountName ?? ""}`, { x: 110, y: 515, size: 8, font: calibriFont });
          targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 95, y: 506, size: 8, font: calibriFont });
          targetPage.drawText(FormatDateToWord(new Date()), { x: 100, y: 495, size: 8, font: calibriFont });
        };
  
        let pages = pdfDoc.getPages();
        let currentPage = pages[0];
        drawHeader(currentPage);
  
        let yPosition = 135;
        const bottomThreshold = 80;
        const lineHeight = 12;
  
        // Helper to handle page breaks
        const addNewPage = async () => {
          const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
          currentPage = pdfDoc.addPage(templatePage);
          drawHeader(currentPage);
          yPosition = 135;
        };
        
        let grandDebit = 0;
        let grandCredit = 0;

        //BANK
        currentPage.drawText(`${FormatValueToDecimal(_data.bankSide?.statementBalance)}`, { x: 270, y: 453, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bankSide?.addTransit)}`, { x: 185, y: 394, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bankSide?.lessOutstanding)}`, { x: 185, y: 326, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bankSide?.adjustedBalance)}`, { x: 285, y: 210, size: 8, font: calibriFont });

        //BOOK
        currentPage.drawText(`${FormatValueToDecimal(_data.bookSide?.glBalance)}`, { x: 640, y: 453, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bookSide?.addMemos)}`, { x: 545, y: 394, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bookSide?.lessMemos)}`, { x: 545, y: 326, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(_data.bookSide?.adjustedBalance)}`, { x: 645, y: 200, size: 8, font: calibriFont });
  
        for (const record of _data.reconcilingItems) {
          // --- PAGE BREAK CHECK FOR ITEM ROW ---
          if (yPosition - lineHeight < bottomThreshold) {
            await addNewPage();
          }

          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(FormatDateToMDY(record.date), { x: 40, y: yPosition, size: 8, font: calibriFont });

          const truncatedRef = truncateText(record?.narrative ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedRef, { x: 335, y: yPosition, size: 8, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(record.debit)}`, { x: 520, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.credit)}`, { x: 650, y: yPosition, size: 8, font: calibriFont });

          yPosition -= lineHeight;

          grandDebit += record.debit
          grandCredit += record.credit
        }
  
        // --- FOOTER SECTION (GRAND TOTAL) ---
        if (yPosition < 100) {
          await addNewPage();
        }
  
        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 765, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`  `, { x: 35, y: yPosition, size: 9, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(grandDebit)}`, { x: 520, y: yPosition, size: 8, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(grandCredit)}`, { x: 650, y: yPosition, size: 8, font: calibriFont });
  
        // --- PAGINATION ---
        const allPages = pdfDoc.getPages();
        const totalPages = allPages.length;
        allPages.forEach((page, index) => {
          page.drawText(`Page ${index + 1} of ${totalPages}`, {
            x: 680,
            y: 580,
            size: 10,
            font: calibriBold,
          });
        });
  
        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `BANK_RECONCILIATION_SUMMARY.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
  
      } catch (error) {
        console.error("Error:", error);
        setFailMessage("Error processing PDF");
      }
      setLoading(false);
  };

  const exportBankReconciliationDetailedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.detailed; // Accessing the detailed object from your API response

      if (!_data || _data?.transactions?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Bank Recon Detailed');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['BANK RECONCILIATION DETAILED']).font = { bold: true, size: 12 };
      worksheet.addRow([`GL Account: ${glAccount.accountNumber ?? ""} - ${glAccount.accountName ?? ""}`]);
      worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Define Table Columns
      const headerRow = worksheet.addRow([
        'Date',
        'Narrative / Description',
        'Debit',
        'Credit',
        'Running Balance',
        'Amt Cleared',
        'Date Cleared',
        'Outstanding Check',
        'Deposit In Transit',
        'Credit Memo',
        'Debit Memo'
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { 
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' } 
        };
      });

      // 5. Add "TOTALS / BEGINNING" Summary Row 
      // (This mimics the data drawn at y: 475 in your PDF)
      const totalsRow = worksheet.addRow([
        '',
        'GRAND TOTALS / SUMMARY',
        parseFloat(_data.totalDebits || 0),
        parseFloat(_data.totalCredits || 0),
        parseFloat(_data.totalBalance || 0),
        parseFloat(_data.totalAmountCleared || 0),
        '',
        parseFloat(_data.totalOutstanding || 0),
        parseFloat(_data.totalInTransit || 0),
        parseFloat(_data.totalCreditMemo || 0),
        parseFloat(_data.totalDebitMemo || 0)
      ]);

      totalsRow.font = { bold: true };
      totalsRow.eachCell((cell, colNumber) => {
        if (colNumber >= 3) {
           cell.numFmt = '#,##0.00';
           cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E8F4FD' } };
        }
      });

      // 6. Add Beginning Balance Row
      const begBalRow = worksheet.addRow([
        '',
        'Beginning Balance',
        '',
        '',
        parseFloat(_data.beginningBalance || 0),
        '', '', '', '', '', ''
      ]);
      begBalRow.getCell(5).numFmt = '#,##0.00';
      begBalRow.font = { italic: true };

      // 7. Add Transaction Data Rows
      _data.transactions.forEach(record => {
        const row = worksheet.addRow([
          FormatDateToMDY(record.date),
          record.narrative ?? "",
          parseFloat(record.debit || 0),
          parseFloat(record.credit || 0),
          parseFloat(record.balance || 0),
          parseFloat(record.amountCleared || 0),
          record.dateCleared ? FormatDateToMDY(record.dateCleared) : "",
          parseFloat(record.outstandingCheck || 0),
          parseFloat(record.depositInTransit || 0),
          parseFloat(record.creditMemo || 0),
          parseFloat(record.debitMemo || 0),
        ]);

        // Apply number formatting to numeric columns
        [3, 4, 5, 6, 8, 9, 10, 11].forEach(colIndex => {
          row.getCell(colIndex).numFmt = '#,##0.00';
        });

        // Alignment for dates and text
        row.getCell(1).alignment = { horizontal: 'center' };
        row.getCell(7).alignment = { horizontal: 'center' };
      });

      // 8. Adjust Column Widths
      worksheet.columns = [
        { width: 12 }, // Date
        { width: 35 }, // Narrative
        { width: 15 }, // Debit
        { width: 15 }, // Credit
        { width: 18 }, // Balance
        { width: 15 }, // Amt Cleared
        { width: 12 }, // Date Cleared
        { width: 18 }, // Outstanding
        { width: 18 }, // In Transit
        { width: 15 }, // Credit Memo
        { width: 15 }, // Debit Memo
      ];

      // 9. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Bank_Reconciliation_Detailed_${monthFrom}_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };

  const exportBankReconciliationSummaryExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
        setFailMessage("Please fill up all fields");
        return;
    }

    setLoading(true);

    try {
        const allData = await fetchReportData();
        const _data = allData.summary; // Accessing the summary object

        if (!_data || (!_data.reconcilingItems && !_data.bankSide)) {
            setFailMessage("No record found");
            setLoading(false);
            return;
        }

        // 1. Create Workbook and Worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Bank Recon Summary');

        // 2. Add Company Header
        worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
        worksheet.addRow([company.address]);
        worksheet.addRow([`TIN: ${company.tin}`]);
        worksheet.addRow([]); // Gap

        // 3. Report Title and Metadata
        worksheet.addRow(['BANK RECONCILIATION SUMMARY']).font = { bold: true, size: 12 };
        worksheet.addRow([`GL Account: ${glAccount.accountNumber ?? ""} - ${glAccount.accountName ?? ""}`]);
        worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
        worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
        worksheet.addRow([]); // Gap

        // 4. SUMMARY SECTION (Two Columns: Bank Side vs Book Side)
        // We use Columns A-B for Bank and D-E for Book
        const summaryStartRow = worksheet.lastRow.number + 1;

        // Headers for the boxes
        worksheet.getCell(`A${summaryStartRow}`).value = 'BALANCE PER BANK STATEMENT';
        worksheet.getCell(`D${summaryStartRow}`).value = 'BALANCE PER GENERAL LEDGER';
        worksheet.getRow(summaryStartRow).font = { bold: true };

        // Data Rows
        const row1 = worksheet.addRow([
            'Statement Balance', parseFloat(_data.bankSide?.statementBalance || 0), 
            '', 
            'GL Balance', parseFloat(_data.bookSide?.glBalance || 0)
        ]);
        
        const row2 = worksheet.addRow([
            'Add: Deposits in Transit', parseFloat(_data.bankSide?.addTransit || 0), 
            '', 
            'Add: Unrecorded Deposits/Credits', parseFloat(_data.bookSide?.addMemos || 0)
        ]);

        const row3 = worksheet.addRow([
            'Less: Outstanding Checks', parseFloat(_data.bankSide?.lessOutstanding || 0) * -1, 
            '', 
            'Less: EWT/NSF/Disbursements', parseFloat(_data.bookSide?.lessMemos || 0) * -1
        ]);

        // const row4 = worksheet.addRow([]); // Gap for visual spacing

        const adjRow = worksheet.addRow([
            'ADJUSTED BANK BALANCE', parseFloat(_data.bankSide?.adjustedBalance || 0), 
            '', 
            'ADJUSTED BOOK BALANCE', parseFloat(_data.bookSide?.adjustedBalance || 0)
        ]);

        // Style Summary Section
        [row1, row2, row3, adjRow].forEach(r => {
            r.getCell(2).numFmt = '#,##0.00';
            r.getCell(5).numFmt = '#,##0.00';
        });
        adjRow.font = { bold: true };
        adjRow.getCell(2).border = { top: { style: 'thin' }, bottom: { style: 'double' } };
        adjRow.getCell(5).border = { top: { style: 'thin' }, bottom: { style: 'double' } };

        worksheet.addRow([]); // Gap before table
        worksheet.addRow([]); // Gap before table

        // 5. RECONCILING ITEMS TABLE (Bottom Table)
        const tableHeader = worksheet.addRow(['Date', 'Account Description / Narrative', 'Debit', 'Credit']);
        tableHeader.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
            cell.alignment = { horizontal: 'center' };
        });

        let grandDebit = 0;
        let grandCredit = 0;

        _data.reconcilingItems.forEach(item => {
            const r = worksheet.addRow([
                FormatDateToMDY(item.date),
                item.narrative ?? "",
                parseFloat(item.debit || 0),
                parseFloat(item.credit || 0)
            ]);
            r.getCell(3).numFmt = '#,##0.00';
            r.getCell(4).numFmt = '#,##0.00';

            grandDebit += (item.debit || 0);
            grandCredit += (item.credit || 0);
        });

        // Footer Total for Reconciling Items
        const totalRow = worksheet.addRow(['', 'GRAND TOTAL', grandDebit, grandCredit]);
        totalRow.font = { bold: true };
        totalRow.getCell(3).numFmt = '#,##0.00';
        totalRow.getCell(4).numFmt = '#,##0.00';
        totalRow.getCell(3).border = { top: { style: 'thin' } };
        totalRow.getCell(4).border = { top: { style: 'thin' } };

        // 6. Set Column Widths
        worksheet.columns = [
            { width: 30 }, // Labels (Bank) / Date
            { width: 20 }, // Amounts (Bank) / Narrative
            { width: 20 },  // Spacer
            { width: 35 }, // Labels (Book) / Debit
            { width: 20 }, // Amounts (Book) / Credit
        ];

        // Override widths specifically for the table section narrative
        worksheet.getColumn(2).width = 45; 

        // 7. Generate and Download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const filename = `Bank_Reconciliation_Summary_${monthFrom}_${year}.xlsx`;
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);

    } catch (error) {
        console.error("Excel Error:", error);
        setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };
//#endregion






const downloadBIRTemplate = (filename) => {
  const link = document.createElement("a");
  link.href = `/docs/${filename}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generate2307 = () => downloadBIRTemplate("BIR-2307.pdf");
const generate2550 = () => downloadBIRTemplate("BIR-2550-DS.pdf");
const generate0619e = () => downloadBIRTemplate("BIR-0619-E.pdf");



//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasBankReconciliation) {
    return <NoAccess />;
  }
//#endregion






//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`BIR Report`}
            </h2>
            <p className="text-gray-500 mt-1">Report or tax return submitted to the Bureau of Internal Revenue (BIR).</p>
          </div>
        </div>

        <div className="hidden grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Account
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Account"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={glAccount?.accountName || ""}
                readOnly
              />
              {!glAccount ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openGLAccountSelectForm}>
                  <ManageSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setGLAccount(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>
          
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month From
            </label>
            <MonthSelect data={monthFrom} setData={setMonthFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month To
            </label>
            <MonthSelect data={monthTo} setData={setMonthTo} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Year
            </label>
            <YearSelect data={year} setData={setYear} isEditable={true}/>
          </div>

        </div>

        <div className="hidden mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">Generate Reports</h2>

          {/* Header Row - Hidden on Mobile */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">Excel Reports</div>
          </div>

          <div className="space-y-3">
            {[
              { 
                label: "Detailed", 
                pdf_summarized: generateBankReconciliationDetailed, 
                excel_summarized: exportBankReconciliationDetailedExcel, // Replace with your Excel function
              },
              { 
                label: "Summary", 
                pdf_summarized: generateBankReconciliationSummary, 
                excel_summarized: exportBankReconciliationSummaryExcel, // Replace with your Excel function
              },
            ].map((group, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                
                {/* Label */}
                <div className="col-span-2">
                  <span className="text-sm font-bold text-gray-700">{group.label}</span>
                </div>

                {/* PDF Column */}
                <div className="col-span-5 flex flex-wrap gap-2">
                  {group.pdf_detailed &&
                    <button 
                      onClick={group.pdf_detailed} 
                      className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition-all active:scale-95"
                      title="Download PDF Detailed"
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Detailed
                    </button>
                  }
                  
                  {group.pdf_summarized &&
                    <button 
                      onClick={group.pdf_summarized} 
                      className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                    >
                      Summarized
                    </button>
                  }

                  {group.pdf_extra && (
                    <button 
                      onClick={group.pdf_extra} 
                      className="flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all active:scale-95"
                    >
                      Summarized w/ %
                    </button>
                  )}
                </div>

                {/* Excel Column */}
                <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                  {group.excel_detailed &&
                    <button 
                      onClick={group.excel_detailed} 
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm transition-all active:scale-95"
                    >
                      Excel Detailed
                    </button>
                  }
                  
                  {group.excel_summarized &&
                    <button 
                      onClick={group.excel_summarized} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ.
                    </button>
                  }
                  
                  
                  { group.excel_extra &&
                    <button 
                      onClick={group.excel_extra} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ. w/ %
                    </button>
                  }
                </div>

              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 px-2">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                BIR Reports
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Download BIR report templates
              </p>
            </div>
          </div>

          {/* Reports */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[
              {
                form: "2307",
                title: "Certificate of Creditable Tax Withheld",
                filename: "BIR-2307.pdf",
                action: generate2307,
              },
              {
                form: "2550-DS",
                title: "Value-Added Tax Declaration",
                filename: "BIR-2550-DS.pdf",
                action: generate2550,
              },
              {
                form: "0619-E",
                title: "Monthly Remittance Return of Income Taxes Withheld",
                filename: "BIR-0619-E.pdf",
                action: generate0619e,
              },
            ].map((report) => (
              <div
                key={report.form}
                className="group border border-gray-200 rounded-xl p-5 hover:border-blue-200 hover:shadow-sm transition-all bg-gray-50/50"
              >
                {/* Top */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-black text-sm">
                      PDF
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-800">
                        BIR Form {report.form}
                      </h3>

                      <p className="text-xs text-gray-500 mt-1">
                        {report.filename}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 mt-4 leading-relaxed min-h-[42px]">
                  {report.title}
                </p>

                {/* Action */}
                <button
                  onClick={report.action}
                  className="mt-5 w-full flex items-center justify-center gap-2
                            bg-blue-600 text-white
                            px-4 py-2.5 rounded-lg
                            text-sm font-bold
                            hover:bg-blue-700
                            active:scale-[0.98]
                            transition-all"
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                  Generate
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ChartOfAccountsSelect 
        visible={showGLAccountSelectForm} 
        onClose={closeGLAccountSelectForm}
        modalName={"Select Account"}
        data={glAccount} setData={setGLAccount}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}